import { createSelector, createSlice } from '@reduxjs/toolkit';
import { RootState } from 'state/store';
import {
  selectTasksIds,
  selectTotalCount,
  selectProjectTasksTableFilters,
} from '../projectTasksListSlice';

interface ExportProjectTaskState {
  selectAllTasks: boolean;
  selectedTasksIds: string[];
  excludedTasksIds: string[];
}

const initialState: ExportProjectTaskState = {
  selectAllTasks: false,
  selectedTasksIds: [],
  excludedTasksIds: [],
};

const exportSlice = createSlice({
  name: 'projectTasks/export',
  initialState,
  reducers: {
    toggleSelectAllTasks: (state) => {
      state.selectAllTasks = !state.selectAllTasks;
      state.excludedTasksIds = [];
      state.selectedTasksIds = [];
    },
    toggleSelectedTask: (state, action) => {
      const taskId = action.payload;
      const areAllTasksSelected = state.selectAllTasks;
      if (areAllTasksSelected) {
        if (state.excludedTasksIds.includes(taskId)) {
          state.excludedTasksIds = state.excludedTasksIds.filter(
            (id) => id !== taskId
          );
        } else {
          state.excludedTasksIds.push(taskId);
        }
      } else {
        if (state.selectedTasksIds.includes(taskId)) {
          state.selectedTasksIds = state.selectedTasksIds.filter(
            (id) => id !== taskId
          );
        } else {
          state.selectedTasksIds.push(taskId);
        }
      }
    },
    resetSelectedTasks: (state) => {
      state.selectAllTasks = false;
      state.selectedTasksIds = [];
      state.excludedTasksIds = [];
    },
  },
});

export const { toggleSelectAllTasks, toggleSelectedTask, resetSelectedTasks } =
  exportSlice.actions;

export const selectProjectTasksExportState = (state: RootState) =>
  state.projectTasksList.export;

export const selectAreAllTasksSelected = (state: RootState) =>
  selectProjectTasksExportState(state).selectAllTasks;

export const selectSelectedTasksIds = (state: RootState) =>
  selectProjectTasksExportState(state).selectedTasksIds;

export const selectExcludedTasksIds = (state: RootState) =>
  selectProjectTasksExportState(state).excludedTasksIds;

export const selectVisibleTasksIds = (state: RootState) =>
  selectTasksIds(state);

export const selectFilteredSelectedTasksIds = createSelector(
  [
    selectSelectedTasksIds,
    selectVisibleTasksIds,
    (state: RootState) => selectProjectTasksTableFilters(state),
  ],
  (selectedTasksIds, visibleTasksIds, filters) => {
    const { search, ...otherFilters } = filters;
    if (Object.keys(otherFilters).length > 0 || (search && search.length > 0)) {
      return selectedTasksIds.filter((id) => visibleTasksIds.includes(id));
    }
    return selectedTasksIds;
  }
);

export const selectAreProjectTasksPartiallySelected = createSelector(
  [
    selectAreAllTasksSelected,
    selectExcludedTasksIds,
    selectSelectedTasksIds,
    selectVisibleTasksIds,
  ],
  (
    areAllTasksSelected,
    excludedTasksIds,
    selectedTasksIds,
    visibleTasksIds
  ) => {
    return areAllTasksSelected
      ? excludedTasksIds.length > 0
      : selectedTasksIds.length > 0 &&
          selectedTasksIds.some((id) => visibleTasksIds.includes(id));
  }
);

export const selectIsTaskSelected = createSelector(
  [selectProjectTasksExportState, (state: RootState, taskId: string) => taskId],
  (state, taskId) => {
    const selectedTasksIds = state.selectedTasksIds;
    const excludedTasksIds = state.excludedTasksIds;
    const areAllTasksSelected = state.selectAllTasks;
    return areAllTasksSelected
      ? !excludedTasksIds.includes(taskId)
      : selectedTasksIds.includes(taskId);
  }
);

export const selectCanExportTasks = createSelector(
  [selectAreProjectTasksPartiallySelected, selectAreAllTasksSelected],
  (areTasksPartiallySelected, areAllTasksSelected) => {
    return areTasksPartiallySelected || areAllTasksSelected;
  }
);

export const selectNumberOfSelectedTasks = createSelector(
  [
    selectFilteredSelectedTasksIds,
    selectExcludedTasksIds,
    selectAreAllTasksSelected,
    (state: RootState) => selectTotalCount(state),
  ],
  (selectedTasksIds, excludedTasksIds, areAllTasksSelected, totalCount) => {
    if (areAllTasksSelected && totalCount) {
      const formattedTotalCount = Number(totalCount);
      if (excludedTasksIds.length > 0) {
        return formattedTotalCount - excludedTasksIds.length;
      }
      return formattedTotalCount;
    }

    return selectedTasksIds.length;
  }
);

export default exportSlice.reducer;
